import { navigateTo, useRouteLocationWithLocale } from '#imports'

import { useIntercom } from '@backmarket/nuxt-layer-care-platform/intercom/composables/useIntercom'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { openModal } from '@ds/components/ModalBase'

import { SALES_CUSTOMER_CARE_SCOPE } from '@/scopes/care-commons/utils/logger/salesCustomerCare/config'
import { useSalesCustomerCareLogger } from '@/scopes/care-commons/utils/logger/salesCustomerCare/useSalesCustomerCareLogger'
import { CUSTOMER_CARE } from '@/scopes/customer-care/route-names'
import type { Orderline } from '~/api/my-orders/types/OrderlineList.types'
import { CUSTOMER_CONVERSATION } from '~/scopes/customer-conversation/route-names'

import { ORDERLINE_CARD_MODAL } from '../OrderlineCard.constants'

export type OrderlineCardActionsConfig = {
  config: {
    [key in Orderline['actions'][number]['type']]: (
      item: Orderline['actions'][number],
    ) => {
      onClick?: ({
        orderlineId,
        publicId,
      }: {
        orderlineId: string | number
        publicId: string
      }) => Promise<void>
      loading?: boolean
    }
  }
}

export const useOrderlineCardActionsConfig = (): OrderlineCardActionsConfig => {
  const { status, intercom, load } = useIntercom()

  const injectLocale = useRouteLocationWithLocale()
  const { logFeatureError } = useSalesCustomerCareLogger()

  const defineRouterLink = ({
    name,
    params,
  }: {
    name: string
    params: Record<string, string | number>
  }) => {
    return toNuxtLink(
      injectLocale({
        type: 'internal',
        name,
        params,
      }),
    )
  }

  return {
    config: {
      cancel_order_before_validation: () => ({
        onClick: async () => {
          /*
           * https://backmarket.atlassian.net/browse/SCAN-5870
           */
          logFeatureError({
            errorDetail:
              "The 'cancel order before validation' action is disabled and cannot be clicked.",
            errorTitle: `${SALES_CUSTOMER_CARE_SCOPE.myOrders} Error on my orders cta config`,
            featureName: 'my_oders_cta_config',
          })
        },
      }),
      cancel_order_pending_payment: () => ({
        onClick: async ({ orderlineId }) => {
          openModal(
            `${ORDERLINE_CARD_MODAL.cancelOrderWithPendingPayment}-${orderlineId}`,
          )
        },
      }),
      diagnosis: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { orderlineId } = item.link.params

            await navigateTo(
              defineRouterLink({
                name: CUSTOMER_CARE.DIAGNOSIS.SELF,
                params: { orderlineId },
              }),
            )
          }
        },
      }),
      diagnosis_new_issue: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { orderlineId } = item.link.params

            await navigateTo(
              defineRouterLink({
                name: CUSTOMER_CARE.DIAGNOSIS.SELF,
                params: { orderlineId },
              }),
            )
          }
        },
      }),
      join_conversation: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { resolutionId } = item.link.params

            const route = CUSTOMER_CONVERSATION.RESOLUTION

            await navigateTo(
              defineRouterLink({
                name: route,
                params: { resolutionId },
              }),
            )
          }
        },
      }),
      live_chat: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { resolutionId } = item.link.params

            await navigateTo(
              defineRouterLink({
                name: CUSTOMER_CONVERSATION.RESOLUTION,
                params: { resolutionId },
              }),
            )
          }
        },
      }),
      open_conversation: (item) => ({
        onClick: async ({ orderlineId }) => {
          if (item?.link && 'params' in item.link) {
            const { conversationId } = item.link.params

            if (conversationId) {
              if (status.value !== 'loaded') {
                await load()
              }

              intercom('showConversation', conversationId)

              await navigateTo({
                query: {
                  intercom_ticket_id: conversationId,
                },
                replace: true,
              })

              return
            }
          }

          logFeatureError({
            errorTitle: `${SALES_CUSTOMER_CARE_SCOPE.resolutionFlow} [Intercom] - Error trying to open showConversation without tiket id`,
            errorDetail: {
              orderlineId,
              actionLink: item?.link,
            },
            featureName: 'resolution_flow_show_intercom_conversation',
          })
        },
        loading:
          status.value === 'awaitingLoad' ? false : status.value !== 'loaded',
      }),
      order_bill: (item) => ({
        onClick: async () => {
          if (!isEmpty(item.link)) {
            window.open(item.link.href, '_blank', 'noopener,noreferrer')
          }
        },
      }),
      pending_order_bill: () => ({}),
      sale_certificate: (item) => ({
        onClick: async () => {
          if (!isEmpty(item.link)) {
            window.open(item.link.href, '_blank', 'noopener,noreferrer')
          }
        },
      }),
      see_return_confirmation: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { resolutionId } = item.link.params

            await navigateTo(
              defineRouterLink({
                name: CUSTOMER_CARE.RESOLUTION.RESOLUTION_RETURN_CONFIRMATION,
                params: { resolutionId },
              }),
            )
          }
        },
      }),
      see_express_replacement_confirmation: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { resolutionId } = item.link.params

            await navigateTo(
              defineRouterLink({
                name: CUSTOMER_CARE.RESOLUTION
                  .RESOLUTION_EXPRESS_REPLACEMENT_CONFIRMATION,
                params: { resolutionId },
              }),
            )
          }
        },
      }),
      track_parcel: (item) => ({
        onClick: async () => {
          if (!isEmpty(item.link)) {
            window.open(item.link.href, '_blank', 'noopener,noreferrer')
          }
        },
      }),
    },
  }
}
